/**
 * Console-side student types for the People API (specs/006-user-management/contracts/
 * account-people-api.md, Students). Students never carry email, phone or photo fields (FR-027).
 */
import { BenchmarkSex, ModificationKind } from "src/models";
import { PageQuery } from "src/models/staff";

export type StudentSource = "roster" | "manual";
export type StudentStatus = "active" | "inactive";

/** Student fields the record form can change (account-service `EDITABLE_STUDENT_FIELDS`). */
export type StudentField =
  | "firstName"
  | "lastName"
  | "benchmarkSex"
  | "birthDate"
  | "ageOverride"
  | "gradeLevel"
  | "externalId"
  | "consentFlags"
  | "schoolId"
  | "active";

/** A class a student is actively enrolled in. */
export interface StudentClassRef {
  id: string;
  name: string;
  source: StudentSource;
}

/** One row of `GET /v1/students`. */
export interface StudentSummary {
  id: string;
  firstName: string;
  lastName: string;
  gradeLevel: string | null;
  schoolId: string;
  schoolName: string | null;
  externalId?: string | null;
  source: StudentSource;
  active: boolean;
  missingScoringData: boolean;
  classes: StudentClassRef[];
}

export interface StudentAllowedActions {
  /** The editable field names. */
  edit: StudentField[];
  enroll: boolean;
  withdraw: boolean;
  move: boolean;
  deactivate: boolean;
  reactivate: boolean;
}

/**
 * `GET`/`PATCH`/`DELETE /v1/students/:id` and `POST …/reactivate`: the roster-student fields plus
 * every `StudentDetail` field. `birthDate` is present only when the district shares birth dates.
 */
export interface StudentDetail extends StudentSummary {
  districtId?: string;
  benchmarkSex: BenchmarkSex | null;
  benchmarkSexSource: "roster" | "school";
  ageYears: number | null;
  ageOverride?: number | null;
  birthDate?: string | null;
  consentFlags: { videoOptOut: boolean; photoOptOut: boolean };
  accommodation?: {
    plannedModification: ModificationKind;
    plannedEventId: string | null;
    plannedNote: string | null;
    healthFlags: string[];
    notes: string | null;
  } | null;
  updatedAt: string;
  readOnlyFields: StudentField[];
  allowedActions: StudentAllowedActions;
}

/** One class membership of a student. */
export interface Enrollment {
  classId: string;
  className: string;
  classSource: StudentSource;
  active: boolean;
  enrolledAt: string;
  withdrawnAt?: string | null;
}

export interface ListStudentsArgs extends PageQuery {
  districtId?: string;
  schoolId?: string;
  classId?: string;
  gradeLevel?: string;
  source?: StudentSource;
  status?: StudentStatus;
  missingScoringData?: boolean;
  /** Teachers: the active students of their schools, for adding to a class. */
  pool?: "school";
}

export type BulkStudentAction = "enroll" | "deactivate" | "reactivate";

export interface BulkStudentsPayload {
  action: BulkStudentAction;
  ids: string[];
  /** Required when `action` is `enroll`. */
  classId?: string;
}

export interface MoveStudentPayload {
  studentId: string;
  fromClassId: string;
  toClassId: string;
}

/** The 409 `POSSIBLE_DUPLICATE` body's `data.candidates`. */
export type DuplicateCandidates = StudentSummary[];

export const studentName = (student: { firstName: string; lastName: string }) =>
  `${student.firstName} ${student.lastName}`.trim();

export const STUDENT_GRADES = [
  "K",
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "10",
  "11",
  "12",
] as const;
